import React, { useMemo, useState } from "react";
import { CalendarCheck, CalendarX, Plane, Users } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Input, Loader, PageHeader, Select, StatCard, TableCard } from "@/components/ui";
import { AttendanceTable, DEPARTMENT_OPTIONS } from "@/components/hrms";
import { currentMonth, monthLabel } from "@/lib/format";

export default function AttendanceAdmin() {
  const { user } = useAuth();
  const [dept, setDept] = useState("all");
  const [month, setMonth] = useState(currentMonth());
  const { data: employees } = useData(() => api.employees.list(user), [user]);
  const { data: records } = useData(() => api.attendance.list({ month }), [month]);

  const empMap = useMemo(() => Object.fromEntries((employees || []).map((e) => [e.id, e])), [employees]);
  const rows = useMemo(
    () => (records || []).filter((r) => empMap[r.employee_id] && (dept === "all" || empMap[r.employee_id].department === dept)),
    [records, empMap, dept],
  );
  const count = (s) => rows.filter((r) => r.status === s).length;

  return (
    <>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard label="Records" value={rows.length} icon={Users} />
        <StatCard label="Present" value={count("Present")} icon={CalendarCheck} color="green" />
        <StatCard label="Absent" value={count("Absent")} icon={CalendarX} color="red" />
        <StatCard label="On Leave" value={count("On Leave")} icon={Plane} color="orange" />
      </div>

      <PageHeader
        description={user.role === "manager" ? "Attendance for your team." : "Attendance across all departments."}
        actions={<>
          <Select value={dept} onChange={(e) => setDept(e.target.value)} options={DEPARTMENT_OPTIONS} className="sm:w-44" aria-label="Department" />
          <Input type="month" value={month} max={currentMonth()} onChange={(e) => e.target.value && setMonth(e.target.value)} className="sm:w-44" aria-label="Month" />
        </>}
      />

      {!records || !employees ? <Loader /> : (
        <TableCard heading={`${rows.length} records`} aside={monthLabel(month)}>
          <AttendanceTable records={rows} employees={empMap} maxRows={150} />
        </TableCard>
      )}
    </>
  );
}
